/**
 * Pure helpers for protocol payloads (`/v1/protocols/`) so Protocol components
 * stay presentational.
 *
 * Protocol shape: `{ id, name, description, url, products[], product_count }`
 * where each product is `{ id, cat_num, name }`.
 * Protocol stub shape (on product detail): `{ id, name, url }`.
 */

/**
 * Label for a linked product, searchable by catalogue number or name.
 *
 * @param {{ cat_num?: string, name?: string, id?: number|string }|null|undefined} product
 * @returns {string} e.g. `"CAT-A-001 — Alpha reagent"`
 */
export function formatProtocolProductLabel(product) {
  if (!product) return '';
  const catNum = String(product.cat_num ?? '').trim();
  const name = String(product.name ?? '').trim();

  if (catNum && name) return `${catNum} — ${name}`;
  if (catNum) return catNum;
  if (name) return name;
  return product.id != null ? `Product ${product.id}` : '';
}

/**
 * Linked products on a protocol payload.
 *
 * @param {Object|null|undefined} protocol
 * @returns {Array<Object>}
 */
export function getProtocolProducts(protocol) {
  return Array.isArray(protocol?.products) ? protocol.products : [];
}

/**
 * Number of linked products.
 *
 * Prefers the backend `product_count`; list rows always carry it, but detail
 * responses refreshed after attach/detach are trusted via `products[]`.
 *
 * @param {Object|null|undefined} protocol
 * @returns {number}
 */
export function getProtocolProductCount(protocol) {
  const count = Number(protocol?.product_count);
  if (Number.isFinite(count) && count >= 0) return count;
  return getProtocolProducts(protocol).length;
}

/**
 * Map products to `{ value, label }` options for the product picker.
 *
 * @param {Array<Object>|null|undefined} products
 * @returns {Array<{ value: number|string, label: string }>}
 */
export function toProductOptions(products) {
  if (!Array.isArray(products)) return [];
  return products
    .filter(product => product && product.id != null)
    .map(product => ({
      value: product.id,
      label: formatProtocolProductLabel(product),
    }));
}

/**
 * Map protocol stubs to `{ value, label }` options.
 *
 * @param {Array<Object>|null|undefined} protocols
 * @returns {Array<{ value: number|string, label: string }>}
 */
export function toProtocolOptions(protocols) {
  if (!Array.isArray(protocols)) return [];
  return protocols
    .filter(protocol => protocol && protocol.id != null)
    .map(protocol => ({
      value: protocol.id,
      label: String(protocol.name ?? `Protocol ${protocol.id}`),
    }));
}

/**
 * Extract the inline `name` error from a 400 payload.
 *
 * Protocol names are unique per lab and there is no pre-submit uniqueness
 * endpoint, so duplicates surface here after the failed save.
 *
 * @param {Object|null|undefined} fieldErrors - `error.response.data` for a 400
 * @returns {string} Empty string when there is no name error.
 */
export function getProtocolNameError(fieldErrors) {
  const nameError = fieldErrors?.name;
  if (!nameError) return '';
  if (Array.isArray(nameError)) {
    return nameError.filter(Boolean).map(String).join(' ');
  }
  return String(nameError);
}

/**
 * Join structured client failure messages into one toast-ready string.
 *
 * @param {{ messages?: Array<string> }|null|undefined} result
 * @param {string} [fallback]
 * @returns {string}
 */
export function getProtocolErrorMessage(
  result,
  fallback = 'An unexpected error occurred. Please try again in a little while.'
) {
  const messages = result?.messages;
  if (Array.isArray(messages) && messages.length) {
    return messages.filter(Boolean).map(String).join(' ');
  }
  return fallback;
}

/**
 * True when the product is a global supplier catalog (Shop) row, which the
 * backend refuses to link to protocols (400).
 *
 * @param {Object|null|undefined} product
 * @returns {boolean}
 */
export function isShopCatalogProduct(product) {
  return product?.supplier_cat_item === true;
}
